import React, { FC, Fragment } from 'react'
import _ from 'lodash'
import { View, StyleSheet } from 'react-native'
import { useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks'
import { color, fontSize, spacing, fontFamily, lineHeight } from '@partipost-cm-new/app-preview/dist/Styles/Base'
import { moderateScale } from '@partipost-cm-new/app-preview/dist/Utils/Scaling'
import PartiText from '@partipost-cm-new/app-preview/dist/Components/PartiText'
import Pill from '@partipost-cm-new/app-preview/dist/Components/Pill'
import ListDivider from '@partipost-cm-new/app-preview/dist/Components/ListDivider'
import RoundCornerButton from '@partipost-cm-new/app-preview/dist/Components/RoundCornerButton'
import { formatAmountWithCurrency } from '@partipost-cm-new/app-preview/dist/Utils'
import MediaViewer, { MediaData } from 'components/MediaViewer'
import Icon from 'particles/Icon'
import PlatformIconList from '../common/PlatformIconList'
import CategoryIconList from '../common/CategoryIconList'
import Requirements from './Requirements'
import { getLanguageCode } from 'config/i18n'
import { selectCampaignByTemplateData } from 'state'
import { selectCampaignInformation } from 'state/appPreview/selector/campaign'
import TaskSchedulePreview from './TaskSchedule'
import { PLATFORM_ENUM } from 'services/api/constants'
import {
    selectDownloadAppPlaceholders,
    selectFollowPlaceholders,
    selectReviewTextImagePlaceholders,
} from 'state/campaignTemplates'
import { ValueObject } from 'state/appPreview'
import PlaceholderComponent from '../common/InterpolationList'
import { interpolate } from 'components/utils/interpolation'
import { selectCountrySetting } from 'state/constants'

const CampaignDetail: FC = () => {
    const locale = getLanguageCode()
    const [t] = useNewTranslation()
    const campaign = useSelector(selectCampaignByTemplateData)
    const campaignInformation = useSelector(selectCampaignInformation)
    const countrySetting = useSelector(selectCountrySetting(campaign?.country))
    const defaultBanner = countrySetting?.default_campaign_image_url
    const [reviewPlatform] = useSelector(selectReviewTextImagePlaceholders)
    const [appName] = useSelector(selectDownloadAppPlaceholders)
    const [followPlatform] = useSelector(selectFollowPlaceholders)
    const minPayout =
        campaign && locale
            ? formatAmountWithCurrency(
                  campaign.currency,
                  campaign.payout_summary ? campaign.payout_summary.min_payout : 0,
                  locale,
              )
            : ''
    const maxPayout =
        campaign && locale
            ? formatAmountWithCurrency(
                  campaign.currency,
                  campaign.payout_summary ? campaign.payout_summary.max_payout : 0,
                  locale,
              )
            : ''
    const label = campaignInformation.registration_enable
        ? t('button.registerForCampaign')
        : t('button.startPartiposting')
    const description =
        campaign?.brand.name && campaign.description
            ? interpolate(campaign.description, {
                  brand_name: campaign?.brand.name,
              })
            : ''
    // to handle placeholder if value empty
    const interpolateList: ValueObject[] = [
        {
            review_platform: reviewPlatform ?? '',
            emptyValue: t('campaignCategory.socialMediaPlatformTitle'),
            isInline: true,
        },
        {
            app_name: appName ?? '',
            emptyValue: t('campaign.template.appName.appPreview.placeholder'),
            isInline: true,
        },
        {
            follow_platform: followPlatform ?? '',
            emptyValue: t('campaign.template.platform.appPreview.placeholder'),
            isInline: true,
        },
    ]
    const isNotUndefinedOrEmpty = (value: string): boolean => {
        return _.isString(value) && !_.isEmpty(value)
    }
    const showListDivider =
        campaign?.platforms &&
        campaign?.platforms.length > 0 &&
        !campaign?.platforms.includes(PLATFORM_ENUM.OTHER) &&
        campaignInformation.categories &&
        campaignInformation.categories.length > 0
    return (
        <Fragment>
            <View style={{ aspectRatio: 4 / 3 }}>
                {campaignInformation.use_default_image ? (
                    <img
                        src={defaultBanner}
                        style={{
                            maxHeight: moderateScale(281) + 'px',
                        }}
                    />
                ) : campaignInformation.images && campaignInformation.images.length > 0 ? (
                    <MediaViewer
                        mediaData={campaignInformation.images as MediaData[]}
                        showPopup={false}
                        showArrowButtons={false}
                        maxHeight={moderateScale(281) + 'px'}
                        showCarouselFooter={false}
                    />
                ) : (
                    <View style={styles.bannerImage}>
                        <PartiText
                            style={{
                                fontFamily: fontFamily.title,
                                fontSize: fontSize.large,
                                color: color.silverChalice,
                            }}
                        >
                            {t('campaign.template.bannerImage.appPreview.placeholder')}
                        </PartiText>
                    </View>
                )}
            </View>
            <View style={styles.campaignTitleContainer}>
                <View style={styles.flexContainer}>
                    <PartiText
                        style={{
                            fontSize: moderateScale(25),
                            color: isNotUndefinedOrEmpty(campaignInformation.title) ? color.gray : color.silverChalice,
                        }}
                    >
                        {isNotUndefinedOrEmpty(campaignInformation.title)
                            ? campaignInformation.title
                            : t('campaign.template.campaignTitle.appPreview.placeholder')}
                    </PartiText>
                    <PartiText style={{ fontFamily: fontFamily.title, fontSize: fontSize.extraLarge }}>
                        {campaign?.brand.name}
                    </PartiText>
                    <View style={{ paddingTop: spacing.small }}>
                        <View style={styles.campaignCategoryContainer}>
                            <Fragment>
                                <PlatformIconList
                                    itemContainerStyle={styles.campaignCategoryItemContainer}
                                    platforms={campaign ? campaign?.platforms : []}
                                    color={color.gray}
                                />
                                {showListDivider && (
                                    <View
                                        style={{
                                            height: moderateScale(10),
                                            borderRightWidth: moderateScale(1),
                                            borderRightColor: color.gray,
                                            marginHorizontal: spacing.small,
                                        }}
                                    />
                                )}
                            </Fragment>
                            <CategoryIconList
                                itemContainerStyle={{
                                    ...styles.campaignCategoryItemContainer,
                                    marginRight: spacing.small,
                                }}
                                categories={campaignInformation.categories}
                                color={color.gray}
                            />
                            <Pill success value={t('statusLabel.submissionOpen')} containerStyle={styles.successPill} />
                        </View>
                    </View>
                </View>
                <View style={styles.brandContainerShadow}>
                    {campaign?.brand.logo_image && (
                        <MediaViewer
                            mediaData={campaign?.brand.logo_image as MediaData}
                            showPopup={false}
                            showArrowButtons={false}
                            maxHeight={moderateScale(52) + 'px'}
                        />
                    )}
                </View>
            </View>
            <View style={styles.payoutDetailContainer}>
                <PartiText style={{ fontFamily: fontFamily.title }}>
                    {t('appCampaign.campaignDetails.maximumPayoutAmountHeader')}
                </PartiText>
                <View style={styles.payoutAmountContainer}>
                    <PartiText
                        style={{
                            fontFamily: fontFamily.title,
                            fontSize: fontSize.extraLarge,
                            color: color.astronaut,
                        }}
                    >
                        {maxPayout}
                    </PartiText>
                    <PartiText style={{ fontSize: fontSize.small, paddingLeft: spacing.extraSmall }}>
                        {t('appCampaign.campaignDetails.minimumPayoutAmountHeader', {
                            minPayout,
                        })}
                    </PartiText>
                </View>
                <View style={styles.viewPayoutIconContainer}>
                    <Icon size="16px" name={'info'} color={'neutralActionActive'} />
                    <PartiText style={{ fontSize: fontSize.small }}>{t('button.viewPayoutDetails')}</PartiText>
                </View>
            </View>
            <View style={styles.campaignDescription}>
                {
                    <PartiText>
                        <PlaceholderComponent template={description} values={interpolateList} />
                    </PartiText>
                }
            </View>
            <ListDivider style={styles.listDivider} />
            <Requirements />

            <TaskSchedulePreview />

            <View style={{ paddingHorizontal: spacing.medium, paddingBottom: spacing.small }}>
                <PartiText
                    style={{
                        fontFamily: fontFamily.title,
                        fontSize: fontSize.extraLarge,
                        paddingBottom: spacing.small,
                    }}
                >
                    {t('appCampaign.campaignDetails.communityGuidelineSectionHeader')}
                </PartiText>
                <PartiText style={{ fontSize: fontSize.medium, lineHeight: lineHeight.copy * fontSize.medium }}>
                    {[
                        t('communityGuideline.beAuthentic'),
                        t('communityGuideline.highQualityPhotos'),
                        t('communityGuideline.noMentionOfCompetitors'),
                        t('communityGuideline.onlyOriginalContent'),
                        t('communityGuideline.noFakeEngagement'),
                        t('communityGuideline.dontDeleteArchivePost'),
                        t('communityGuideline.noProfanities'),
                        t('communityGuideline.dontContactBrandsDirectly'),
                    ]
                        .map(rule => '\u2022 ' + rule)
                        .join('\n')}
                </PartiText>
                <View style={{ paddingVertical: spacing.medium }}>
                    <PartiText
                        style={{
                            textDecorationLine: 'none',
                            lineHeight: lineHeight.copy * fontSize.large,
                            color: color.astronaut,
                        }}
                    >
                        {t('appCampaign.campaignDetails.communityGuidelineLink')}
                    </PartiText>
                </View>
            </View>
            <View style={styles.shadowContainer}>
                <RoundCornerButton
                    disabled={true}
                    customStyle={styles.startButtonStyle}
                    customText={styles.startTextStyle}
                >
                    {label.toUpperCase()}
                </RoundCornerButton>
            </View>
        </Fragment>
    )
}

const styles = StyleSheet.create({
    viewPayoutIconContainer: {
        flexDirection: 'row',
        paddingTop: spacing.extraSmall,
        alignItems: 'center',
        justifyContent: 'center',
        gap: moderateScale(4),
    },
    payoutAmountContainer: { flexDirection: 'row', alignItems: 'center' },
    payoutDetailContainer: { paddingHorizontal: spacing.medium, alignItems: 'flex-start' },
    campaignCategoryContainer: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' },
    campaignCategoryItemContainer: {
        paddingVertical: spacing.extraSmall,
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    flexContainer: { flex: 1 },
    campaignTitleContainer: { flexDirection: 'row', justifyContent: 'space-between', padding: spacing.medium },
    successPill: {
        backgroundColor: color.fern,
    },
    brandContainerShadow: {
        borderRadius: 8,
        shadowColor: color.shadow,
        shadowOffset: {
            width: 1,
            height: 1,
        },
        shadowOpacity: 1,
        shadowRadius: 1,
        elevation: 1,
        width: moderateScale(52) + 'px',
        height: moderateScale(52) + 'px',
    },
    listDivider: {
        marginHorizontal: spacing.medium,
        marginVertical: spacing.medium,
    },
    campaignDescription: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        paddingHorizontal: spacing.medium,
        paddingTop: spacing.medium,
    },
    shadowContainer: {
        padding: spacing.small,
        backgroundColor: color.white,
        shadowColor: color.shadow,
        shadowOffset: {
            width: 0,
            height: 8,
        },
        shadowRadius: moderateScale(10),
        shadowOpacity: 1,
        elevation: 6,
        borderColor: color.shadow,
        borderWidth: 0.2,
    },
    startButtonStyle: {
        borderRadius: moderateScale(5),
        alignItems: 'center',
        height: moderateScale(45),
        width: '100%',
        backgroundColor: color.christine,
    },
    startTextStyle: {
        color: color.white,
        fontSize: moderateScale(16),
        fontFamily: fontFamily.title,
    },
    bannerImage: {
        backgroundColor: color.concrete,
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
})

export default CampaignDetail
